import {
  Column,
  Table,
  CustomContextMenuItem,
  DragAndDroppableProps,
  EditableProps,
  FloatingActionBar,
  FloatingActionBarProps,
  HighlightableProps,
  MultiSelectableProps,
  Row,
  ScrollableProps,
  SortableProps,
} from '@ardoq/table';
import {
  Button,
  ButtonType,
  GhostButton,
  PrimaryButton,
  SecondaryButton,
  TertiaryButton,
  UnstyledButton,
} from '@ardoq/button';
import { SearchInput, TextInput } from '@ardoq/forms';
import { Extend } from '@ardoq/common-helpers';
import { colors } from '@ardoq/design-tokens';
import { ButtonWithDropdown } from '@ardoq/dropdown-menu';
import { DropdownButtonOptionType } from '@ardoq/dropdown-menu-ui';
import { Icon, IconName } from '@ardoq/icons';
import { Box, FlexBox } from '@ardoq/layout';
import {
  AnimatedModalDialog,
  ModalContainer,
  ModalFooter,
  ModalHeader,
  ModalLayout,
  ModalSize,
  ModalWrapper,
} from '@ardoq/modal';
import { Island } from '@ardoq/page-layout';
import {
  TableFilterBar,
  TableFilterBarLabels,
  TableFilterExpressionFrom,
  TableFilterList,
  TableFilterString,
} from '@ardoq/table-filter-bar';
import { Text } from '@ardoq/typography';
import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { WithUncontrolledPopover } from '@ardoq/popovers';
import { ArdoqId } from '@ardoq/api-types';
import { type EntityAttachmentType } from './attachmentPreview';
import {
  DuplicatesModal,
  type DuplicatesModalProps,
} from './duplicates/DuplicatesModal';
import type { FindDuplicatesButtonProps } from './duplicates/toDuplicatesModalProps';

const useDragover = (
  onDrop: (e: DragEvent) => void,
  onDragEnter: VoidFunction,
  onDragLeave: VoidFunction
) => {
  const isDraggingOverRef = useRef(false);

  useEffect(() => {
    const handleDragEnter = (e: DragEvent) => {
      e.preventDefault();

      // Only trigger callback if we're entering from outside the document
      // relatedTarget is null when entering from outside, or an element when moving between children
      const element = e.relatedTarget as Node | null;
      const isEnteringFromOutside =
        !element || !document.body.contains(element);

      if (isEnteringFromOutside && !isDraggingOverRef.current) {
        isDraggingOverRef.current = true;

        if (e.dataTransfer?.types.includes('Files')) {
          onDragEnter();
        }
      }
    };

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();

      // Only trigger callback if we're leaving to outside the document
      // relatedTarget is null when leaving the document, or an element when moving between children
      const element = e.relatedTarget as Node | null;
      const isLeavingToOutside = !element || !document.body.contains(element);

      if (isLeavingToOutside && isDraggingOverRef.current) {
        isDraggingOverRef.current = false;
        onDragLeave();
      }
    };

    const handleDragover = (e: DragEvent) => {
      // Prevents opening image in a new tab
      e.preventDefault();
    };

    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
      isDraggingOverRef.current = false;

      onDragLeave();
      onDrop(e);
    };

    document.addEventListener('dragenter', handleDragEnter);
    document.addEventListener('dragleave', handleDragLeave);
    document.addEventListener('drop', handleDrop);
    document.addEventListener('dragover', handleDragover);

    return () => {
      document.removeEventListener('dragenter', handleDragEnter);
      document.removeEventListener('dragleave', handleDragLeave);
      document.removeEventListener('drop', handleDrop);
      document.removeEventListener('dragover', handleDragover);
    };
  }, [onDrop, onDragEnter, onDragLeave]);
};

const DropZoneButton = styled(UnstyledButton)`
  width: 100%;

  &:hover {
    cursor: pointer;
    background-color: ${colors.surfaceActionGhostHover};
  }
`;

export type DropZoneProps = {
  hovered: boolean;
  dragActive: boolean;
  text: string;
  uploadFilesAriaLabel: string;
  onDragEnter: VoidFunction;
  onDragLeave: VoidFunction;
  onDrop: (files: File[]) => void;
};

export const DropZone = (props: DropZoneProps) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useDragover(
    event => {
      const files = Array.from(event.dataTransfer?.files || []);
      props.onDrop(files);
    },
    props.onDragEnter,
    props.onDragLeave
  );

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      props.onDrop(Array.from(files));
      // Reset the input value to allow uploading the same file again if needed
      event.target.value = '';
    }
  };

  return (
    <>
      <DropZoneButton
        onClick={() => fileInputRef.current?.click()}
        aria-label={props.uploadFilesAriaLabel}
      >
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          multiple
          onChange={handleFileChange}
          accept="*/*"
        />
        <FlexBox
          align="center"
          justify="center"
          padding="large"
          gap="medium"
          borderRadius="medium"
          style={{
            border: `2px dashed ${
              props.hovered
                ? colors.borderBrandStrong
                : props.dragActive
                  ? colors.brand200
                  : colors.borderDisabled
            }`,
            backgroundColor: props.hovered
              ? colors.brand200
              : props.dragActive
                ? colors.brand100
                : 'transparent',
            transition: 'background-color 0.2s, border-color 0.2s',
          }}
        >
          <Icon
            iconName={IconName.FILE_UPLOADED}
            size={48}
            color={colors.icon}
          />
          <Text variant="text1Bold" color="textDefault">
            {props.text}
          </Text>
        </FlexBox>
      </DropZoneButton>
    </>
  );
};

export type DeleteConfirmationModalProps = {
  isOpen: boolean;
  summary: DeleteConfirmationSummaryProps;
  headerText: string;
  cancelButtonText: string;
  deleteButtonText: string;
  onConfirm: VoidFunction;
  onCancel: VoidFunction;
};

export const DeleteConfirmationModal = (
  props: DeleteConfirmationModalProps
) => (
  <AnimatedModalDialog
    isOpen={props.isOpen}
    onClickOutside={props.onCancel}
    onEscPress={props.onCancel}
  >
    <ModalContainer modalSize={ModalSize.M}>
      <ModalHeader onCloseButtonClick={props.onCancel}>
        {props.headerText}
      </ModalHeader>
      <ModalWrapper>
        <ModalLayout>
          <DeleteSummary {...props.summary} />
        </ModalLayout>
      </ModalWrapper>
      <ModalFooter>
        <Button onClick={props.onCancel} buttonType={ButtonType.SECONDARY}>
          {props.cancelButtonText}
        </Button>
        <Button onClick={props.onConfirm} buttonType={ButtonType.DANGER}>
          {props.deleteButtonText}
        </Button>
      </ModalFooter>
    </ModalContainer>
  </AnimatedModalDialog>
);
export type DeleteConfirmationSummaryProps = {
  folderCount: number;
  attachmentCount: number;
  rootFolders: DeleteFolderProps[];
  rootAttachments: DeleteAttachmentProps[];
  summaryText: string;
};

const DeleteSummary = (props: DeleteConfirmationSummaryProps) => (
  <div>
    <p>{props.summaryText}</p>
    <ul>
      {props.rootFolders.map(folder => (
        <DeleteFolder key={folder.name} {...folder} />
      ))}
      {props.rootAttachments.map(attachment => (
        <DeleteAttachment key={attachment.name} {...attachment} />
      ))}
    </ul>
  </div>
);

export type DeleteFolderProps = {
  type: 'folder';
  name: string;
  children: (DeleteFolderProps | DeleteAttachmentProps)[];
};

const DeleteFolder = (props: DeleteFolderProps) => (
  <li>
    {props.name}
    {props.children.length > 0 && (
      <ul>
        {props.children.map((child, index) =>
          child.type === 'folder' ? (
            <DeleteFolder key={index} {...child} />
          ) : (
            <DeleteAttachment key={index} {...child} />
          )
        )}
      </ul>
    )}
  </li>
);

export type DeleteAttachmentProps = {
  type: 'attachment';
  name: string;
};

const DeleteAttachment = (props: DeleteAttachmentProps) => (
  <li>{props.name}</li>
);

const FolderOptionButton = styled(UnstyledButton)<{
  $isSelected: boolean;
  $depth: number;
}>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  padding-left: ${({ $depth }) => 12 + $depth * 24}px;
  border-radius: 4px;
  background-color: ${({ $isSelected }) =>
    $isSelected ? colors.surfaceActionGhostPressed : 'transparent'};

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background-color: ${({ $isSelected }) =>
      $isSelected
        ? colors.surfaceActionGhostPressed
        : colors.surfaceActionGhostHover};
  }
`;

export type NewFolderInputEditingProps = {
  name: string;
  onNameChange: (name: string) => void;
  onCancel: VoidFunction;
};

export type NewFolderInputProps = {
  onStartEditing: VoidFunction;
  editing: NewFolderInputEditingProps | null;
  buttonLabel: string;
  inputPlaceholder: string;
};

const NewFolderInput = (props: NewFolderInputProps) => {
  if (!props.editing) {
    return (
      <GhostButton onClick={props.onStartEditing}>
        <FlexBox gap="small" align="center">
          <Icon iconName={IconName.ADD} color={colors.icon} />
          <Text variant="text1">{props.buttonLabel}</Text>
        </FlexBox>
      </GhostButton>
    );
  }

  return (
    <FlexBox gap="small" align="center">
      <Icon iconName={IconName.FOLDER} color={colors.icon} />
      <Box flexGrow={1}>
        <TextInput
          value={props.editing.name}
          onValueChange={props.editing.onNameChange}
          placeholder={props.inputPlaceholder}
          autoFocus
        />
      </Box>
      <GhostButton onClick={props.editing.onCancel}>
        <Icon iconName={IconName.CLOSE} color={colors.icon} />
      </GhostButton>
    </FlexBox>
  );
};

export type MoveToFolderOption = {
  id: ArdoqId | 'root';
  name: string;
  depth: number;
  isDisabled: boolean;
  onClick: VoidFunction;
};

export type MoveToModalProps = {
  isOpen: boolean;
  folderOptions: MoveToFolderOption[];
  headerText: string;
  cancelButtonText: string;
  moveButtonText: string;
  createAndMoveButtonText: string;
  searchPlaceholder: string;
  folderDisabledTitle: string;
  createNewFolderButtonText: string;
  newFolderNamePlaceholder: string;
  selectedDestinationFolderId: ArdoqId | 'root';
  search: {
    value: string;
    onValueChange: (value: string) => void;
  };
  newFolderInput: Omit<NewFolderInputProps, 'buttonLabel' | 'inputPlaceholder'>;
  isConfirmDisabled: boolean;
  onConfirm: VoidFunction;
  onCancel: VoidFunction;
};

export const MoveToModal = (props: MoveToModalProps) => (
  <AnimatedModalDialog
    isOpen={props.isOpen}
    onClickOutside={props.onCancel}
    onEscPress={props.onCancel}
  >
    <ModalContainer modalSize={ModalSize.M}>
      <ModalHeader onCloseButtonClick={props.onCancel}>
        {props.headerText}
      </ModalHeader>
      <ModalWrapper>
        <ModalLayout>
          <FlexBox flexDirection="column" gap="small">
            <SearchInput
              value={props.search.value}
              onValueChange={props.search.onValueChange}
              placeholder={props.searchPlaceholder}
            />
            <FlexBox
              flexDirection="column"
              gap="xxsmall"
              style={{ maxHeight: '300px', overflowY: 'auto' }}
            >
              {props.folderOptions.map(option => (
                <FolderOptionButton
                  key={option.id}
                  $isSelected={props.selectedDestinationFolderId === option.id}
                  $depth={option.depth}
                  disabled={option.isDisabled}
                  title={
                    option.isDisabled ? props.folderDisabledTitle : undefined
                  }
                  onClick={option.onClick}
                >
                  <Icon
                    iconName={
                      option.id === 'root' ? IconName.HOME : IconName.FOLDER
                    }
                    color={colors.icon}
                  />
                  <Text variant="text1">{option.name}</Text>
                </FolderOptionButton>
              ))}
            </FlexBox>
            <NewFolderInput
              {...props.newFolderInput}
              buttonLabel={props.createNewFolderButtonText}
              inputPlaceholder={props.newFolderNamePlaceholder}
            />
          </FlexBox>
        </ModalLayout>
        <ModalFooter>
          <Button onClick={props.onCancel} buttonType={ButtonType.SECONDARY}>
            {props.cancelButtonText}
          </Button>
          <PrimaryButton
            onClick={props.onConfirm}
            isDisabled={props.isConfirmDisabled}
          >
            {props.newFolderInput.editing
              ? props.createAndMoveButtonText
              : props.moveButtonText}
          </PrimaryButton>
        </ModalFooter>
      </ModalWrapper>
    </ModalContainer>
  </AnimatedModalDialog>
);

export type DocumentArchiveViewProps = {
  title: string;
  table: DocumentArchiveTableViewProps;
  dropZone: DropZoneProps;
  deleteConfirmationModal: DeleteConfirmationModalProps;
  moveToModal: MoveToModalProps;
};

export type DocumentArchiveDuplicatesProps = {
  findButton: FindDuplicatesButtonProps | null;
  modal: DuplicatesModalProps;
};

export type DocumentArchivePageViewProps = DocumentArchiveViewProps & {
  duplicates: DocumentArchiveDuplicatesProps | null;
};

const FindDuplicatesButton = (props: FindDuplicatesButtonProps) => (
  <FlexBox flexDirection="column" align="end" gap="xsmall" maxWidth="320px">
    <SecondaryButton onClick={props.onClick} isLoading={props.isLoading}>
      {props.label}
    </SecondaryButton>
    {props.loadingHint && (
      <Text variant="caption" color="textSubtle" align="right">
        {props.loadingHint}
      </Text>
    )}
  </FlexBox>
);

export const DocumentArchivePageView = (
  props: DocumentArchivePageViewProps
) => (
  <>
    <Island
      fullHeight
      title={props.title}
      rightContent={
        props.duplicates?.findButton && (
          <FindDuplicatesButton {...props.duplicates.findButton} />
        )
      }
    >
      <DropZone {...props.dropZone} />
      <DocumentArchiveTableView {...props.table} />
    </Island>
    <DeleteConfirmationModal {...props.deleteConfirmationModal} />
    <MoveToModal {...props.moveToModal} />
    {props.duplicates && <DuplicatesModal {...props.duplicates.modal} />}
  </>
);

type CreateFolderButtonProps = {
  onClick: VoidFunction;
  label: string;
};

const CreateFolderButton = (props: CreateFolderButtonProps) => (
  <TertiaryButton onClick={props.onClick}>{props.label}</TertiaryButton>
);

export type TablePrimaryActionButtonProps = {
  label: string;
  disabled: boolean;
  popover: null | TablePrimaryActionButtonPopoverProps;
  onClick: VoidFunction;
};

type TablePrimaryActionButtonPopoverProps = {
  items: { key: string; name: string; onClick: VoidFunction }[];
  message: string;
};

const MoveEntitiesPopover = (props: TablePrimaryActionButtonPopoverProps) => (
  <FlexBox flexDirection="column" maxWidth="300px">
    <Text variant="text2">{props.message}</Text>
    <ul>
      {props.items.map(item => (
        <li key={item.key}>
          <FlexBox align="center">
            <GhostButton onClick={item.onClick}>{item.name}</GhostButton>
          </FlexBox>
        </li>
      ))}
    </ul>
  </FlexBox>
);

const TablePrimaryActionButton = (props: TablePrimaryActionButtonProps) => {
  const popover = props.popover;

  if (!popover) {
    return (
      <PrimaryButton onClick={props.onClick} isDisabled={props.disabled}>
        {props.label}
      </PrimaryButton>
    );
  }

  return (
    <WithUncontrolledPopover content={<MoveEntitiesPopover {...popover} />}>
      <PrimaryButton onClick={props.onClick} isDisabled={props.disabled}>
        {props.label}
      </PrimaryButton>
    </WithUncontrolledPopover>
  );
};

export type GroupedEntityType = 'folder' | EntityAttachmentType;

export type ContextMenuItem = CustomContextMenuItem<GroupedEntity> & {
  label: string;
  buttonType?: DropdownButtonOptionType;
};

export type GroupedEntity = {
  id: string;
  actions: {
    items: ContextMenuItem[];
  };
  type: GroupedEntityType;
  _group: {
    name: string;
    isFolder: boolean;
    isExpanded: boolean;
    icon: IconName;
    thumbnailUrl: string | null;
  };
  size: {
    displayValue: string | null;
    bytes: number | null;
  };
  lastModified: {
    displayValue: string | null;
    timestamp: string | null;
  };
  modifiedBy: string | null;
  uri: string | null;
};

const Thumbnail = styled.img`
  width: 24px;
  height: 24px;
  object-fit: cover;
  border-radius: 2px;
  flex-shrink: 0;
`;

const FileOrFolderNameCell = (props: GroupedEntity['_group']) => (
  <FlexBox gap="small" align="center">
    {props.thumbnailUrl ? (
      <Thumbnail src={props.thumbnailUrl} alt="" />
    ) : (
      <Icon iconName={props.icon} color={colors.icon} />
    )}
    {props.name}
  </FlexBox>
);

const ActionsCell = (data: GroupedEntity['actions']) => (
  <FlexBox height="full" align="center" justify="flex-start">
    <ButtonWithDropdown
      options={data.items.map(({ icon, ...item }) => ({
        ...item,
        leftIcon: icon,
      }))}
      hideChevron
      buttonType={ButtonType.GHOST}
    >
      <Icon iconName={IconName.MORE_VERT} color={colors.icon} />
    </ButtonWithDropdown>
  </FlexBox>
);

type DocumentArchiveFilters = [
  Extend<TableFilterString, { id: 'filename' }>,
  Extend<TableFilterList, { id: 'attachmentType' }>,
];

export type FilterExpression =
  TableFilterExpressionFrom<DocumentArchiveFilters>;

export type DocumentArchiveFloatingActionBarProps = Omit<
  FloatingActionBarProps,
  'anchorRef'
>;

export type DocumentArchiveTableViewProps = {
  tableHeight?: string;
  rows: Row<GroupedEntity>[];
  floatingActionBar: DocumentArchiveFloatingActionBarProps | null;
  columnTitles: {
    fileName: string;
    size: string;
    lastModified: string;
    modifiedBy: string;
  };
  searchPlaceholder: string;
  createFolderLabel: string;
  search: {
    value: string;
    onValueChange: (value: string) => void;
  };
  filter: {
    filters: DocumentArchiveFilters;
    optionCounts: Record<
      string,
      Record<string, number | undefined> | undefined
    >;
    filterExpressions: FilterExpression[];
    onFilterExpressionChange: (filterExpressions: FilterExpression[]) => void;
    labels: TableFilterBarLabels;
  };
  grouping: {
    expandedIds: string[];
    expandFolder: (id: string) => void;
    closeFolder: (id: string) => void;
    renameRow: (id: string, newName: string) => void;
  };
  scrollable: ScrollableProps;
  dragAndDroppable: DragAndDroppableProps<GroupedEntity>;
  selectable: MultiSelectableProps;
  sortable: SortableProps;
  editable: EditableProps;
  tablePrimaryActionButton: TablePrimaryActionButtonProps | null;
  createFolder: Omit<CreateFolderButtonProps, 'label'>;
  highlightable: HighlightableProps;
  compareSizeInBytes: (
    a: GroupedEntity['size'] | null,
    b: GroupedEntity['size'] | null
  ) => number;
};

export const DocumentArchiveTableView = (
  props: DocumentArchiveTableViewProps
) => {
  const tableAnchorRef = useRef<HTMLDivElement>(null);
  const columns: Column<GroupedEntity>[] = [
    {
      title: props.columnTitles.fileName,
      id: '_group',
      expandedIds: props.grouping.expandedIds,
      flex: 3,
      onRowExpansionToggle: (rowId, expanded) => {
        if (expanded) {
          props.grouping.expandFolder(rowId);
        } else {
          props.grouping.closeFolder(rowId);
        }
      },
      valueGetter: data => data.name,
      InnerCellRenderer: FileOrFolderNameCell,
      onCellValueChange: params =>
        props.grouping.renameRow(params.row.id, params.value),
    },
    {
      id: 'size',
      title: props.columnTitles.size,
      align: 'right',
      comparator: props.compareSizeInBytes,
      cellRenderer: size => size.displayValue,
      valueGetter: size => size.displayValue ?? '',
      minWidth: 120, // Allows space for column name and left aligned sort indicator without truncating column header
      maxWidth: 200, // Prevent excessively wide column when displayValue is very long (e.g. "1.5 GB")
    },
    {
      id: 'lastModified',
      title: props.columnTitles.lastModified,
      flex: 1,
      minWidth: 170, // Allows space for column header name without truncation
      cellRenderer: lastModified => lastModified?.displayValue ?? null,
      valueGetter: lastModified => lastModified?.displayValue ?? '',
      comparator: (a, b) => {
        if (a?.timestamp === b?.timestamp) return 0;
        if (a?.timestamp === null || a?.timestamp === undefined) return 1;
        if (b?.timestamp === null || b?.timestamp === undefined) return -1;
        return a.timestamp < b.timestamp ? -1 : 1;
      },
    },
    {
      id: 'modifiedBy',
      title: props.columnTitles.modifiedBy,
      flex: 1,
      minWidth: 150, // Allows space for column header name without truncation
    },
    {
      id: 'actions',
      title: '',
      suppressHeaderContextMenu: true,
      suppressSorting: true,
      resizable: false,
      maxWidth: 106,
      minWidth: 106,
      cellRenderer: ActionsCell,
      valueGetter: actions => actions.items.map(item => item.label).join(', '),
    },
  ];

  return (
    <FlexBox
      flexDirection="column"
      height="full"
      gap="medium"
      paddingY="medium"
    >
      <FlexBox justify="space-between" align="center" gap="medium">
        <SearchInput
          value={props.search.value}
          onValueChange={props.search.onValueChange}
          placeholder={props.searchPlaceholder}
        />
        <FlexBox gap="small">
          {props.tablePrimaryActionButton && (
            <TablePrimaryActionButton {...props.tablePrimaryActionButton} />
          )}
          <CreateFolderButton
            {...props.createFolder}
            label={props.createFolderLabel}
          />
        </FlexBox>
      </FlexBox>
      <TableFilterBar {...props.filter} />
      <TableAnchor ref={tableAnchorRef}>
        <Table
          height={props.tableHeight}
          columns={columns}
          rows={props.rows}
          dragAndDroppable={props.dragAndDroppable}
          selectable={props.selectable}
          sortable={props.sortable}
          editable={props.editable}
          scrollable={props.scrollable}
          highlightable={props.highlightable}
        />
        {props.floatingActionBar && (
          <FloatingActionBar
            {...props.floatingActionBar}
            anchorRef={tableAnchorRef}
          />
        )}
      </TableAnchor>
    </FlexBox>
  );
};

const TableAnchor = styled.div`
  position: relative;
  min-height: 400px;
  flex-grow: 1;
`;
